"use client";
import { FC, useEffect, useId, useState } from "react";

import { PortableText, PortableTextProps } from "@portabletext/react";

import { useCurrentLocale } from "lib/utils";

interface StackedLineBarLineProps {
  /**
   * The index of the line.
   */
  index: number;

  /**
   * A mutable ref object for the first list item element.
   */
  listItemRef?: React.MutableRefObject<HTMLLIElement | null>;

  translations: {
    failedTests: string;
    okTests: string;
    website: string;
  };
  /**
   * The value of the line.
   */
  value: number;
}
const StackedLineBarLine: FC<StackedLineBarLineProps> = ({
  index,
  listItemRef,
  translations,
  value,
}) => {
  const t = translations;
  // Initialize state with all 0 values
  const [stateValue, setStateValue] = useState(0);
  const locale = useCurrentLocale();

  // Update state with actual values from props after initial render
  useEffect(() => {
    setTimeout(() => {
      setStateValue(value);
    }, 0);
  }, [value]);

  return (
    <li
      className="m-0 flex flex-row items-center gap-1 p-0 focus-visible:outline-none"
      key={`${value}-${index}`}
      ref={index === 0 ? listItemRef : undefined}
      tabIndex={-1}
    >
      <span
        aria-hidden
        className="not:more-contrast:opacity-70 min-w-[1.25rem] @xs:min-w-[2rem]"
      >
        {index + 1}
      </span>
      <div className="relative bottom-0 h-3 flex-1 rounded-[0.15rem] border border-darkestBlue/40 bg-[#e0e0e0] more-contrast:border-darkestBlue/80 dark:border-lightblue/30 dark:bg-[#e0e0e0]/40 more-contrast:dark:border-lightblue/60">
        <div
          className="absolute left-0 top-0 h-full rounded-sm bg-green transition-width duration-1000 reduce-motion:duration-0 forced-colors:bg-[CanvasText]"
          style={{ width: `${stateValue}%` }}
        />
        <div
          className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-sm bg-darkestBlue transition-[left] duration-1000 reduce-motion:duration-0 dark:border-2 dark:border-lightblue dark:bg-slate-900"
          style={{ left: stateValue === 0 ? "1.1rem" : `${stateValue}%` }}
        >
          <span className="p-1 px-2 text-sm font-semibold text-white">
            <span className="sr-only">{`${t.website} ${index + 1}: ${value}% ${t.okTests}`}</span>
            <span aria-hidden>{`${value}${locale === "no" ? " " : ""}%`}</span>
          </span>
        </div>
      </div>
    </li>
  );
};

interface StackedLineBarProps {
  /**
   * The caption text displayed below the stacked line bar.
   */
  caption?: PortableTextProps["value"];

  /**
   * The CSS class name applied to the stacked line bar.
   */
  className?: string;

  /**
   * The HTML element used for the outermost wrapper of the stacked line bar.
   * Can be 'aside', 'section', or 'div'.
   */
  component?: "aside" | "section" | "div";

  /**
   * A React ref object pointing to the first item in the stacked line bar.
   */
  firstItemRef?: React.MutableRefObject<HTMLLIElement | null>;

  /**
   * The heading text displayed above the stacked line bar.
   */
  heading?: string;

  /**
   * The label displayed on the left side of the stacked line bar.
   */
  leftLabel?: string;

  /**
   * The label displayed on the right side of the stacked line bar.
   */
  rightLabel?: string;

  translations: {
    failedTests: string;
    okTests: string;
    website: string;
  };
  /**
   * An array of numbers representing the values for the stacked line bar.
   */
  values: Array<number>;
}
export const StackedLineBar: FC<StackedLineBarProps> = ({
  caption,
  className,
  component = "aside",
  firstItemRef,
  heading,
  leftLabel,
  rightLabel,
  translations,
  values,
}) => {
  const id = useId();
  const t = translations;

  const labels = {
    left: leftLabel || t.okTests,
    right: rightLabel || t.failedTests,
  };
  const Wrapper = component;

  return (
    <Wrapper
      aria-labelledby={
        heading && component !== "div" ? `${id}-heading` : undefined
      }
      className={`not-prose space-y-3 ${className || ""}`}
    >
      {heading && (
        <h3
          className="wrap-text font-semibold xl:text-2xl"
          id={`${id}-heading`}
        >
          {heading}
        </h3>
      )}
      <div className="flex flex-col @container">
        {values && (
          <>
            <div
              aria-hidden
              className="mb-3 hidden flex-row flex-wrap justify-between @sm:flex"
            >
              <span className="text-base font-semibold">{labels.left}</span>
              <span className="text-base font-semibold">{labels.right}</span>
            </div>
            <ul className="m-0 flex list-none flex-col gap-1 p-0 @container">
              {values.map((value, index) => (
                <StackedLineBarLine
                  index={index}
                  key={`${value}-${index}`}
                  listItemRef={index === 0 ? firstItemRef : undefined}
                  translations={translations}
                  value={value}
                />
              ))}
            </ul>
          </>
        )}
        {caption && (
          <div className="prose prose-sm mt-4 max-w-[initial] opacity-90 dark:prose-invert md:prose-p:text-right [&>p]:text-base [&>p]:md:text-right">
            <PortableText value={caption} />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default StackedLineBar;
